import { createContext, useContext, ReactNode } from "react";

export interface AnalyticsLocationContextValue {
  location: string;
  additionalData?: Record<string, any>;
}

const AnalyticsLocationContext =
  createContext<AnalyticsLocationContextValue | null>(null);

export interface AnalyticsLocationProviderProps {
  children: ReactNode;
  location: string;
  additionalData?: Record<string, any>;
}

/**
 * Provider that gives child components access to their location context for analytics.
 * Wrap components with this to provide location information that components
 * can automatically access without requiring explicit props.
 *
 * @example
 * <AnalyticsLocationProvider location="card-details-view">
 *   <ExternalLinkWrapper cardId="123">
 *     <h1>Card Title</h1>
 *   </ExternalLinkWrapper>
 * </AnalyticsLocationProvider>
 */
export const AnalyticsLocationProvider = ({
  children,
  location,
  additionalData,
}: AnalyticsLocationProviderProps) => {
  return (
    <AnalyticsLocationContext.Provider value={{ location, additionalData }}>
      {children}
    </AnalyticsLocationContext.Provider>
  );
};

/**
 * Hook to access the current analytics location context.
 * Returns null if not inside an AnalyticsLocationProvider.
 */
export const useAnalyticsLocation =
  (): AnalyticsLocationContextValue | null => {
    return useContext(AnalyticsLocationContext);
  };
